<template>
  <div class="l-page l-page--message">
    <!-- サイドバーエリア -->
    <page-sidebar />
    <!-- /サイドバーエリア -->
    <main class="l-main">
      <div class="l-main-head">
        <div class="c-title u-w600">
          <div class="c-title__text c-title__text--blue u-fs34">ユニークキー配信の成果</div>
        </div>
      </div>
      <div class="l-main-content">
        <div class="p-message-result">
          <div class="unique-key-result-date">
            <button type="button" class="c-btn c-btn--gray u-w120 u-h40 unique-key-result-date__prev" @click="onMoveDay(-1)">
              <p class="c-btn__text">前の日</p>
            </button>
            <input
              id="uniqueKeyResultDate"
              v-model="sendDate"
              class="unique-key-result-date__input"
              type="date"
              :max="today"
              @change="onDateChange"
            />
            <button
              type="button"
              class="c-btn c-btn--gray u-w120 u-h40 unique-key-result-date__next"
              :disabled="sendDate >= today"
              @click="onMoveDay(1)"
            >
              <p class="c-btn__text">次の日</p>
            </button>
          </div>
          <p class="unique-key-result-note">
            この日に送った配信の成果です。表示数・クリック数は毎朝集計し、送った日から3日間は数え直します。
          </p>

          <dl v-if="totals" class="unique-key-result-totals">
            <div>
              <dt>配信件数</dt>
              <dd>{{ formatCount(totals.message_count) }}</dd>
            </div>
            <div>
              <dt>配信数</dt>
              <dd>{{ formatCount(totals.distribution_count) }}</dd>
            </div>
            <div>
              <dt>表示数</dt>
              <dd>{{ formatCount(totals.display_count) }}</dd>
            </div>
            <div>
              <dt>クリック数</dt>
              <dd>{{ formatCount(totals.click_count) }}</dd>
            </div>
            <div>
              <dt>クリック率</dt>
              <dd>{{ formatRate(totals.click_count, totals.display_count) }}</dd>
            </div>
          </dl>

          <div
            class="p-message-result__data"
            :class="{ 'is-loading': isLoading, 'is-empty': isEmpty, 'is-loaded': !isEmpty && isLoaded }"
          >
            <div class="c-loader c-loader"></div>
            <div class="c-empty">
              <div class="c-empty__text">この日に送った配信はありません</div>
            </div>
            <table class="c-table">
              <thead>
                <tr>
                  <th class="c-table__cell--center">管理用タイトル</th>
                  <th class="c-table__cell--center is-sortable u-w180">
                    <span class="c-table__text">配信日時</span>
                    <sort-switch name="sendStartDate" :order="orderOf('sendStartDate')" @on-click="onSortClick" />
                  </th>
                  <th class="c-table__cell--center u-w70">作成元</th>
                  <th class="c-table__cell--center">リスト</th>
                  <th class="c-table__cell--center is-sortable u-w110">
                    <span class="c-table__text">配信数</span>
                    <sort-switch name="distributionCount" :order="orderOf('distributionCount')" @on-click="onSortClick" />
                  </th>
                  <th class="c-table__cell--center is-sortable u-w110">
                    <span class="c-table__text">表示数</span>
                    <sort-switch name="displayCount" :order="orderOf('displayCount')" @on-click="onSortClick" />
                  </th>
                  <th class="c-table__cell--center is-sortable u-w110">
                    <span class="c-table__text">クリック数</span>
                    <sort-switch name="clickCount" :order="orderOf('clickCount')" @on-click="onSortClick" />
                  </th>
                  <th class="c-table__cell--center u-w110">クリック率</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="message in messages" :key="message.message_id" class="unique-key-result-row">
                  <td class="c-table__cell">
                    <a class="unique-key-result-row__name" :href="detailUrl(message)">{{ message.message_name }}</a>
                  </td>
                  <td class="c-table__cell c-table__cell--center">{{ getDisplayDateTime(message.send_start_date) }}</td>
                  <td class="c-table__cell c-table__cell--center">{{ message.is_api ? 'API' : 'CMS' }}</td>
                  <td class="c-table__cell">{{ message.list_name || '-' }}</td>
                  <td class="c-table__cell c-table__cell--right">{{ formatCount(message.distribution_count) }}</td>
                  <td class="c-table__cell c-table__cell--right">{{ formatCount(message.display_count) }}</td>
                  <td class="c-table__cell c-table__cell--right">{{ formatCount(message.click_count) }}</td>
                  <td class="c-table__cell c-table__cell--right">
                    {{ formatRate(message.click_count, message.display_count) }}
                  </td>
                </tr>
              </tbody>
            </table>
            <pagination :pagination-info="paginationInfo" @on-page-click="onPageClick($event)" />
          </div>
        </div>
      </div>
    </main>

    <error-modal :is-active="errorMessage !== ''" :message="errorMessage" :on-close-click="onCloseErrorModal" />
  </div>
</template>

<script>
import axios from 'axios';
import dayjs from 'dayjs';

export default {
  data() {
    const today = dayjs().format('YYYY-MM-DD');
    return {
      today,
      // 当日はまだ送信中・未集計のことが多いので、前日を既定にする
      sendDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      sort: 'sendStartDate',
      // -1: 降順 (既定), 1: 昇順
      order: -1,
      page: 1,
      totals: null,
      messages: [],
      paginationInfo: null,
      isLoading: false,
      isLoaded: false,
      isEmpty: false,
      errorMessage: '',
      requestSeq: 0,
    };
  },
  mounted: function () {
    this.loadData();
  },
  methods: {
    loadData: function () {
      this.isLoading = true;
      this.isLoaded = false;
      // 日付や並び順を続けて変えたとき、遅れて届いた古い応答で表示を上書きしない
      const seq = ++this.requestSeq;
      const params = { send_date: this.sendDate, sort: this.sort, order: this.order, page: this.page };
      axios
        .get('/api/unique-key-message/result', { params })
        .then((res) => {
          if (seq !== this.requestSeq) {
            return;
          }
          this.isLoading = false;
          if (res.data.result !== 'OK') {
            this.errorMessage = res.data.message;
            return;
          }
          this.totals = res.data.totals;
          this.messages = res.data.messages.data;
          this.paginationInfo = res.data.messages;
          this.isLoaded = true;
          this.isEmpty = this.messages.length < 1;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) {
            return;
          }
          this.handleHttpError(error);
          this.isLoading = false;
          this.errorMessage = '成果の取得に失敗しました。再度お試しください。';
        });
    },
    orderOf: function (name) {
      return this.sort === name ? this.order : 0;
    },
    detailUrl: function (message) {
      return '/coins-admin/unique-key-message/review?message_id=' + message.message_id;
    },
    formatCount: function (value) {
      return value === null || value === undefined ? '-' : Number(value).toLocaleString();
    },
    formatRate: function (clickCount, displayCount) {
      if (!displayCount) {
        return '-';
      }
      return ((Number(clickCount) / Number(displayCount)) * 100).toFixed(2) + '%';
    },
    onDateChange: function () {
      if (!this.sendDate) {
        return;
      }
      this.page = 1;
      this.loadData();
    },
    onMoveDay: function (days) {
      this.sendDate = dayjs(this.sendDate).add(days, 'day').format('YYYY-MM-DD');
      this.onDateChange();
    },
    // 同じ列なら向きを変え、別の列なら降順から始める
    onSortClick: function (name) {
      this.order = this.sort === name ? -this.order : -1;
      this.sort = name;
      this.page = 1;
      this.loadData();
    },
    onPageClick: function (page) {
      this.page = page;
      this.loadData();
    },
    onCloseErrorModal: function () {
      this.errorMessage = '';
    },
  },
};
</script>

<style scoped>
.unique-key-result-date {
  display: flex;
  align-items: center;
  gap: 12px;
}
.unique-key-result-date__input {
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d5dde4;
  border-radius: 4px;
  background: #fff;
}
.unique-key-result-note {
  color: #666;
  font-size: 13px;
  margin: 8px 0 16px;
}
.unique-key-result-totals {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}
.unique-key-result-totals div {
  background: #fff;
  border: 1px solid #dde3e8;
  padding: 12px 16px;
}
.unique-key-result-totals dt {
  color: #666;
  font-size: 13px;
}
.unique-key-result-totals dd {
  font-size: 24px;
  font-weight: bold;
  font-variant-numeric: tabular-nums;
}
.unique-key-result-row__name {
  color: #1d6fdc;
  text-decoration: underline;
  word-break: break-all;
}
td.c-table__cell--right {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
